import { useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageTitle from "@/components/base/PageTitle";

const shapes = ["Round", "Oval", "Emerald", "Princess", "Cushion", "Pear", "Marquise", "Asscher", "Radiant", "Not Sure"];
const colors = ["D (Colorless)", "E", "F", "G", "H", "I", "J", "Not Sure"];
const clarities = ["FL", "IF", "VVS1", "VVS2", "VS1", "VS2", "SI1", "SI2", "Not Sure"];

const FORM_ACTION = "https://readdy.ai/api/form/d83sc2u6qilughrrc2hg";

export default function RequestDiamond() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const heroRef = useScrollReveal<HTMLDivElement>();
  const formRef = useScrollReveal<HTMLDivElement>();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") {
        params.append(key, value);
      }
    });

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: params.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        form.reset();
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || `Submission failed (${response.status})`);
      }
    } catch (err) {
      setSubmitError(
        `Failed to send: ${err instanceof Error ? err.message : "Please try again."}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zayan-bg flex flex-col">
      <PageTitle title="Request a Diamond - Zayan Atelier" />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-[130px] md:pt-[170px] overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[60vh]">
          {/* Left Content */}
          <div className="flex flex-col justify-center px-8 lg:px-16 py-16 lg:py-24 reveal" ref={heroRef}>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-zayan-gold text-xs tracking-[0.2em] font-sans">DIAMOND SOURCING</span>
              <div className="h-px w-12 bg-zayan-gold" />
            </div>
            <h2 className="font-serif text-4xl lg:text-6xl text-zayan-gold leading-tight mb-6">
              Request a Diamond
            </h2>
            <div className="h-px w-16 bg-zayan-gold mb-8" />
            <p className="text-zayan-textMuted text-sm leading-relaxed max-w-md">
              Share your preferences and let Zayan Atelier curate a selection of IGI-certified lab-grown diamonds tailored to your exact specifications.
            </p>
          </div>

          {/* Right Image */}
          <div className="relative overflow-hidden h-64 sm:h-80 lg:h-auto">
            <img
              src="https://readdy.ai/api/search-image?query=exquisite%20diamond%20solitaire%20engagement%20ring%20with%20brilliant%20round%20cut%20diamond%20in%20elegant%20gold%20setting%2C%20placed%20on%20deep%20black%20velvet%20surface%2C%20dramatic%20studio%20lighting%20with%20sparkling%20bokeh%20highlights%2C%20luxury%20jewelry%20photography%2C%20ultra%20high%20detail%2C%20rich%20warm%20gold%20tones%20contrasting%20against%20dark%20background%2C%20romantic%20and%20timeless%20composition&width=700&height=700&seq=request-hero-v2&orientation=portrait"
              alt="Diamond engagement ring on black velvet"
              className="w-full h-full object-cover object-top"
              onError={(e) => {
                const target = e.currentTarget;
                target.onerror = null;
                target.src = "https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=700&h=700&fit=crop&q=80";
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-zayan-bg/60 to-transparent" />
          </div>
        </div>
      </section>

      {/* Craft Disclaimer */}
      <section className="border-t border-zayan-border px-6 lg:px-16 pt-12 md:pt-16">
        <div className="max-w-5xl mx-auto">
          <div className="border border-zayan-border bg-zayan-bgCard p-6 md:p-8 flex items-start gap-4 md:gap-5">
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full border border-zayan-gold/40 shrink-0">
              <i className="ri-hand-heart-line text-zayan-gold text-lg md:text-xl" />
            </div>
            <div>
              <h3 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-2">
                THE NATURE OF CUSTOM WORK
              </h3>
              <p className="text-zayan-textMuted text-sm leading-relaxed">
                Custom orders are built around your vision. Reference images, renders, and previews are guides — not guarantees. Natural variations in gemstone character, metal finish, and handcrafted production are inherent to the process and part of what makes each piece one of a kind. These are not defects. They are craft.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="border-t border-zayan-border py-16 md:py-20 px-6 lg:px-16">
        <div className="max-w-5xl mx-auto reveal" ref={formRef}>
          <div className="text-center mb-12">
            <p className="text-zayan-gold text-xs tracking-[0.2em] uppercase mb-3 font-sans">
              Your Preferences
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-zayan-gold mb-4">
              Tell Us What You Are Looking For
            </h2>
            <p className="text-zayan-textMuted text-sm max-w-xl mx-auto">
              The more details you provide, the better we can curate options that match your vision.
            </p>
          </div>

          <form
            id="request-diamond-form"
            action={FORM_ACTION}
            method="POST"
            data-readdy-form="request-diamond"
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Name */}
            <div className="flex flex-col gap-2">
              <label className="text-zayan-text text-xs tracking-wider">Full Name *</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                  <i className="ri-user-line text-zayan-gold/60 text-sm" />
                </div>
                <input name="name" type="text" required placeholder="Your full name" className="w-full bg-zayan-bg border border-zayan-border text-zayan-text text-sm pl-10 pr-4 py-3 outline-none focus:border-zayan-gold transition-colors" />
              </div>
            </div>

            {/* Email */}
            <div className="flex flex-col gap-2">
              <label className="text-zayan-text text-xs tracking-wider">Email Address *</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                  <i className="ri-mail-line text-zayan-gold/60 text-sm" />
                </div>
                <input name="email" type="email" required placeholder="Your email address" className="w-full bg-zayan-bg border border-zayan-border text-zayan-text text-sm pl-10 pr-4 py-3 outline-none focus:border-zayan-gold transition-colors" />
              </div>
            </div>

            {/* Phone */}
            <div className="flex flex-col gap-2">
              <label className="text-zayan-text text-xs tracking-wider">Phone Number</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                  <i className="ri-phone-line text-zayan-gold/60 text-sm" />
                </div>
                <input name="phone" type="tel" placeholder="Your phone number" className="w-full bg-zayan-bg border border-zayan-border text-zayan-text text-sm pl-10 pr-4 py-3 outline-none focus:border-zayan-gold transition-colors" />
              </div>
            </div>

            {/* Shape */}
            <div className="flex flex-col gap-2">
              <label className="text-zayan-text text-xs tracking-wider">Preferred Shape</label>
              <div className="relative">
                <select name="shape" className="w-full bg-zayan-bg border border-zayan-border text-zayan-textMuted text-sm px-4 py-3 pr-10 outline-none focus:border-zayan-gold transition-colors appearance-none cursor-pointer">
                  <option value="">Select a shape</option>
                  {shapes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-zayan-gold text-lg" />
                </div>
              </div>
            </div>

            {/* Carat */}
            <div className="flex flex-col gap-2">
              <label className="text-zayan-text text-xs tracking-wider">Carat Size</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                  <i className="ri-vip-diamond-line text-zayan-gold/60 text-sm" />
                </div>
                <input name="carat" type="text" placeholder="e.g., 1.5 carats" className="w-full bg-zayan-bg border border-zayan-border text-zayan-text text-sm pl-10 pr-4 py-3 outline-none focus:border-zayan-gold transition-colors" />
              </div>
            </div>

            {/* Color */}
            <div className="flex flex-col gap-2">
              <label className="text-zayan-text text-xs tracking-wider">Color Grade</label>
              <div className="relative">
                <select name="color" className="w-full bg-zayan-bg border border-zayan-border text-zayan-textMuted text-sm px-4 py-3 pr-10 outline-none focus:border-zayan-gold transition-colors appearance-none cursor-pointer">
                  <option value="">Select a color</option>
                  {colors.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-zayan-gold text-lg" />
                </div>
              </div>
            </div>

            {/* Clarity */}
            <div className="flex flex-col gap-2">
              <label className="text-zayan-text text-xs tracking-wider">Clarity Grade</label>
              <div className="relative">
                <select name="clarity" className="w-full bg-zayan-bg border border-zayan-border text-zayan-textMuted text-sm px-4 py-3 pr-10 outline-none focus:border-zayan-gold transition-colors appearance-none cursor-pointer">
                  <option value="">Select a clarity</option>
                  {clarities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-zayan-gold text-lg" />
                </div>
              </div>
            </div>

            {/* Budget */}
            <div className="flex flex-col gap-2 md:col-span-2">
              <label className="text-zayan-text text-xs tracking-wider">Budget Range</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                  <i className="ri-money-dollar-circle-line text-zayan-gold/60 text-sm" />
                </div>
                <input name="budget" type="text" placeholder="e.g., $3,000 - $5,000" className="w-full bg-zayan-bg border border-zayan-border text-zayan-text text-sm pl-10 pr-4 py-3 outline-none focus:border-zayan-gold transition-colors" />
              </div>
            </div>

            {/* Notes */}
            <div className="flex flex-col gap-2 md:col-span-2">
              <label className="text-zayan-text text-xs tracking-wider">Additional Notes</label>
              <textarea name="notes" rows={4} maxLength={500} placeholder="Any specific requirements, setting preferences, or questions?" className="bg-zayan-bg border border-zayan-border text-zayan-text text-sm px-4 py-3 outline-none focus:border-zayan-gold transition-colors resize-none" />
              <span className="text-zayan-textDarker text-xs">Max 500 characters</span>
            </div>

            {/* Submit */}
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-zayan-gold text-zayan-bg py-3 text-xs tracking-[0.15em] font-sans hover:bg-zayan-goldLight transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "SUBMITTING..." : "SUBMIT REQUEST"}
              </button>
            </div>

            {submitted && (
              <p className="md:col-span-2 text-zayan-gold text-sm text-center gold-shimmer flex items-center justify-center gap-2">
                <i className="ri-check-line text-zayan-gold" />
                Thank you! Your request has been submitted. We will be in touch shortly.
              </p>
            )}
            {submitError && (
              <p className="md:col-span-2 text-red-400 text-sm text-center">{submitError}</p>
            )}
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}